/**
 * S22. The reminder ladder. `/deadline`
 *
 * THIS IS NOT PRODUCT AND THE SCREEN SAYS SO OUT LOUD. Screen spec 3 cuts S22
 * from the clickable prototype and delivers it as three flat comps, and the
 * reasoning is worth repeating rather than quietly working around: these three
 * artifacts live in email and in the place the project was built. Building
 * them inside the SV Cloud shell would put school furniture inside the product
 * we just decided is not school-shaped. So this route renders a comp sheet:
 * three depictions of messages, with the frame drawn around them, on a page
 * that is not reachable from any nav.
 *
 * THE LADDER IS THREE RUNGS AND ONE ACTION. Thirty days, seven days, forty
 * eight hours, and every rung's action is the same words pointing at the same
 * link, because there has only ever been one thing to do. What escalates is
 * the sentence, not the button. A ladder that invented a different verb per
 * rung would be theatre, and by the third message a person would be reading
 * the button instead of the deadline.
 *
 * EVERY RUNG CARRIES BOTH DATES, and they are two different dates on purpose.
 * The link expiring is recoverable: ask for another one. The project being
 * removed is not. Collapsing them into one "deadline" is how a person ends up
 * believing they have three more days than they have, or three fewer.
 *
 * EVERY RUNG ALSO CARRIES THE SENTENCE ABOUT GITHUB. It is the same sentence
 * three times and it is not padding: the actual fear behind a deletion notice
 * is that the work disappears, and the work is in the repository. Saying it
 * once in the first email and then dropping it from the urgent one would be
 * withholding the reassurance at the exact moment it is needed.
 *
 * THE FRAME NEVER IMITATES A REAL SENDER. Nothing here carries a company
 * signature block, a real address, or an unsubscribe footer. It is a comp of a
 * message, drawn as a comp, so it cannot be mistaken for a message.
 *
 * The dates are computed from the same window the seeded claim uses, so this
 * sheet and the claim screen name the same two days and stay right however
 * long the prototype is left running. The message copy is fixture, not state,
 * which is why it lives in this file rather than in the seed: none of it is
 * anything the product knows about a project.
 */

import { useEffect } from 'react';
import { Link } from 'react-router';

import { Button } from '../../components/primitives/Button';

import { dateLabel } from '../../components/patterns/DeployRow';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Wordmark } from '../../components/brand/Wordmark';

import { t } from '../../lib/i18n/t';
import { CLEANUP_GRACE_DAYS } from '../../lib/mock/seed';
import { captureEvent } from '../../lib/analytics/posthog';

import '../../styles/screens/graduation.css';

const DAY = 24 * 60 * 60 * 1000;
const HOUR = 60 * 60 * 1000;

/**
 * The project these messages are about, and its window.
 *
 * Written here rather than seeded because none of it is project state: a
 * message that has not been sent yet is not a fact the product holds about
 * Soi Cats. The window mirrors the seeded claim's twenty three days so the
 * sheet and `/claim/9f2c-8d41-7a06` print the same two dates.
 */
const SUBJECT = {
  name: 'Soi Cats',
  token: '9f2c-8d41-7a06',
  host: 'soi-cats.sv-academy.org',
  windowDays: 23,
};

interface Rung {
  id: string;
  /** How far ahead of the link expiring this one goes out. */
  lead: string;
  /** Where it lands. Two of the three are email and nothing else. */
  channel: string;
  /** Milliseconds before the link expires. The send date is derived, never typed. */
  before: number;
  subject: string;
  body: string[];
  action: string;
}

const LADDER: Rung[] = [
  {
    id: 'thirty-days',
    lead: '30 days before',
    channel: 'Email',
    before: 30 * DAY,
    subject: '{project} is yours to keep',
    body: [
      'Your project is finished and it is still running at {host}. You can move it to your own SV Cloud account whenever you like.',
      'It takes about a minute, your app does not go offline, and your web address does not change. Your link works until {expires}.',
      'If nobody claims it, the cleanup removes this project on {cleanup}. Your code stays in GitHub.',
    ],
    action: 'Claim {project}',
  },
  {
    id: 'seven-days',
    lead: '7 days before',
    channel: 'Email',
    before: 7 * DAY,
    subject: '7 days left to claim {project}',
    body: [
      'Your claim link expires on {expires}. After that you can still ask for a new one, but it is one more thing to do.',
      '{host} is still answering, and everything in your database and your files is still there.',
      'If nobody claims it, the cleanup removes this project on {cleanup}. Your code stays in GitHub.',
    ],
    action: 'Claim {project}',
  },
  {
    id: 'forty-eight-hours',
    lead: '48 hours before',
    channel: 'Email, and a notice where you built it',
    before: 48 * HOUR,
    subject: 'Last reminder: {project} comes down on {cleanup}',
    body: [
      'This is the last one we send. Your claim link expires on {expires}.',
      'If nobody claims it, the cleanup removes {project} on {cleanup} and {host} stops working. Your database and your files go with it, and that cannot be undone.',
      'Your code stays in GitHub. We do not touch your repository.',
    ],
    action: 'Claim {project}',
  },
];

/* ------------------------------------------------------------------ screen */

export default function DeadlineNotice() {
  const expiry = Date.now() + SUBJECT.windowDays * DAY;
  const expiresAt = new Date(expiry).toISOString();
  const cleanupAt = new Date(expiry + CLEANUP_GRACE_DAYS * DAY).toISOString();

  const vars = {
    project: SUBJECT.name,
    host: SUBJECT.host,
    expires: dateLabel(expiresAt),
    cleanup: dateLabel(cleanupAt),
  };

  const claimPath = `/claim/${encodeURIComponent(SUBJECT.token)}`;

  useEffect(() => {
    captureEvent('graduation_deadline_notice_viewed', {
      subject_project: SUBJECT.name,
      window_days: SUBJECT.windowDays,
    });
  }, []);

  return (
    <div className="grad">
      <div className="grad__lockup">
        <Wordmark size={32} />
      </div>

      <header className="grad__head">
        <h1 className="grad__title">{t('Before the claim link expires')}</h1>
        <p className="grad__say">
          {t(
            'Three messages go out before a project is removed. These are comps of them, not the product: two are email, and the last one is also a notice in the place the project was built.',
          )}
        </p>
      </header>

      <SectionPanel
        title={t('The two dates')}
        note={t('They are different dates, and every message names both.')}
      >
        <dl className="gledger__rows">
          <div className="gledger__row">
            <dt className="gledger__key">{t('Claim link expires')}</dt>
            <dd className="gledger__value mono">{vars.expires}</dd>
          </div>
          <div className="gledger__row">
            <dt className="gledger__key">{t('Project is removed')}</dt>
            <dd className="gledger__value mono">{vars.cleanup}</dd>
          </div>
        </dl>
      </SectionPanel>

      <ol className="dline">
        {LADDER.map((rung) => (
          <li className="dline__rung" key={rung.id}>
            <div className="dline__meta">
              <span className="dline__lead">{t(rung.lead)}</span>
              <span className="dline__chip mono">{t(rung.channel)}</span>
              <span className="dline__when">
                {dateLabel(new Date(expiry - rung.before).toISOString())}
              </span>
            </div>

            {/* The frame is drawn as a comp and never as a real message: no
                sender, no signature, no unsubscribe line. A depiction that
                could be mistaken for the artifact is not a depiction. */}
            <article className="dmail">
              <h2 className="dmail__subject">{t(rung.subject, vars)}</h2>
              <div className="dmail__body">
                {rung.body.map((line) => (
                  <p className="dmail__line" key={line}>
                    {t(line, vars)}
                  </p>
                ))}
              </div>
              <div className="dmail__act">
                <Link to={claimPath}>
                  <Button variant="outline">{t(rung.action, vars)}</Button>
                </Link>
              </div>
            </article>
          </li>
        ))}
      </ol>

      <p className="grad__foot">
        {t('This page is a comp sheet. It is not linked from anywhere in the product.')}
      </p>
    </div>
  );
}
